import { useState, useEffect, useCallback, useRef } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSignIn, useSignUp } from "@clerk/tanstack-react-start";
import { isClerkAPIResponseError } from "@clerk/tanstack-react-start/errors";
import type { ClerkAPIError, EmailCodeFactor, SignInFirstFactor } from "@clerk/types";
import { AtSign, Mail } from "lucide-react";
import { useAuth } from "@clerk/tanstack-react-start";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import {
  OnboardingButton,
  OnboardingCard,
  OnboardingDescription,
  OnboardingDivider,
  OnboardingInput,
  OnboardingLabel,
  OnboardingLayout,
  OnboardingTerms,
} from "@/components/onboarding-layout";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { getOnboardingStatusServer } from "@/server/onboarding";
import { acceptInvitationAndJoinServer } from "@/server/workspace-invitations";
import { logInfo, logWarn, logError } from "@/lib/structured-log";
import { extractApiErrorMessage } from "@/lib/api/error";
import {
  buildOnboardingReturnPath,
  extractNewTaskSearchFromReturnTo,
  sanitizeAuthReturnTo,
} from "@/lib/routing/auth-return";

type AuthFlow = "sign-in" | "sign-up" | null;

const isEmailCodeFactor = (factor: SignInFirstFactor): factor is EmailCodeFactor => {
  return factor.strategy === "email_code";
};

const isIdentifierNotFoundError = (error: unknown) => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const errors = (error as { errors?: ClerkAPIError[] }).errors;
  return Array.isArray(errors) && errors.some(({ code }) => code === "form_identifier_not_found");
};

const isSessionAlreadyExistsError = (error: unknown): boolean => {
  const values: string[] = [];

  if (error instanceof Error) {
    values.push(error.message);
  }

  if (error && typeof error === "object") {
    const errors = (error as { errors?: ClerkAPIError[] }).errors;
    if (Array.isArray(errors)) {
      for (const clerkError of errors) {
        if (clerkError.code) values.push(clerkError.code);
        if (clerkError.message) values.push(clerkError.message);
      }
    }
  }

  return values.some((value) => {
    const normalized = value.toLowerCase();
    return (
      normalized.includes("session already exists") ||
      (normalized.includes("session") && normalized.includes("exist"))
    );
  });
};

function toErrorPayload(error: unknown, t?: (key: string) => string) {
  if (isClerkAPIResponseError(error)) {
    return { errors: error.errors };
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  try {
    return { error: JSON.stringify(error) };
  } catch {
    return { error: t ? t("pages:auth.unserializableError") : "unserializable-error" };
  }
}

// localStorage key for persisting invitation token through SSO flow
const PENDING_INVITATION_KEY = "pending_workspace_invitation";
// localStorage key for persisting registration invite code through SSO flow
const PENDING_REGISTRATION_INVITE_KEY = "pending_registration_invite";

interface SigninSearchParams {
  join_workspace?: string;
  invite?: string;
  return_to?: string;
}

export const Route = createFileRoute("/signin/")({
  // No beforeLoad guard needed:
  // - Unauthenticated users can access this page (it's the signin page)
  // - Authenticated users are redirected client-side by the useEffect in AuthPage component
  validateSearch: (search: Record<string, unknown>): SigninSearchParams => ({
    join_workspace: typeof search.join_workspace === "string" ? search.join_workspace : undefined,
    invite: typeof search.invite === "string" ? search.invite : undefined,
    return_to:
      typeof search.return_to === "string" ? sanitizeAuthReturnTo(search.return_to) : undefined,
  }),
  component: AuthPage,
});

function AuthPage() {
  const { t } = useTranslation(["pages", "common"]);
  const navigate = useNavigate();
  const { join_workspace, invite, return_to } = Route.useSearch();
  const auth = useAuth();
  const seconds = 60;
  const { isLoaded: isAuthLoaded } = auth;
  const { isLoaded: isSignInLoaded, signIn, setActive: setActiveSignIn } = useSignIn();
  const { isLoaded: isSignUpLoaded, signUp, setActive: setActiveSignUp } = useSignUp();

  const [verifying, setVerifying] = useState(false);
  const [authFlow, setAuthFlow] = useState<AuthFlow>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<ClerkAPIError[]>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [left, setLeft] = useState(seconds);
  const [resendStatus, setResendStatus] = useState(false);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const postAuthNavigationStartedRef = useRef(false);
  const verificationSubmittingRef = useRef(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Handle accepting invitation and joining workspace
  const handleAcceptInvitation = useCallback(
    async (token: string): Promise<boolean> => {
      setIsAcceptingInvitation(true);
      try {
        logInfo("auth.invitation.accepting", { token_prefix: token.slice(0, 8) });
        const result = await acceptInvitationAndJoinServer({ data: { token } });

        // Clear the pending invitation from localStorage
        try {
          localStorage.removeItem(PENDING_INVITATION_KEY);
        } catch {
          // localStorage might be disabled, ignore
        }

        logInfo("auth.invitation.accepted", {
          workspace_id: result.workspace_id,
          workspace_slug: result.workspace_slug,
        });

        // Navigate to the default workspace entry
        navigate({
          to: "/workspace/$workspaceSlug/new-task",
          params: { workspaceSlug: result.workspace_slug ?? "" },
          search: extractNewTaskSearchFromReturnTo(return_to),
        });
        return true;
      } catch (error) {
        // Clear invalid token from localStorage
        try {
          localStorage.removeItem(PENDING_INVITATION_KEY);
        } catch {
          // localStorage might be disabled, ignore
        }

        const errorMessage = extractApiErrorMessage(
          error,
          t("pages:auth.invitationFailed", "Failed to accept invitation"),
        );

        logError("auth.invitation.failed", {
          error: errorMessage,
          token_prefix: token.slice(0, 8),
        });

        // Show user-friendly error message
        toast.error(errorMessage);
        return false;
      } finally {
        setIsAcceptingInvitation(false);
      }
    },
    [navigate, return_to, t],
  );

  // Save invitation token to localStorage for SSO flow
  useEffect(() => {
    if (join_workspace) {
      try {
        localStorage.setItem(PENDING_INVITATION_KEY, join_workspace);
        logInfo("auth.invitation.token_saved", {
          token_prefix: join_workspace.slice(0, 8),
        });
      } catch {
        // localStorage might be disabled, ignore
        logWarn("auth.invitation.token_save_failed", {
          reason: "localStorage unavailable",
        });
      }
    }
  }, [join_workspace]);

  // Save registration invite code to localStorage for SSO flow
  useEffect(() => {
    if (invite) {
      try {
        localStorage.setItem(PENDING_REGISTRATION_INVITE_KEY, invite);
        logInfo("auth.registration_invite.saved", {
          code_prefix: invite.slice(0, 4),
        });
      } catch {
        // localStorage might be disabled, ignore
      }
    }
  }, [invite]);

  // Handle client-side authentication state sync
  // This fixes the issue where SSR route guard can't read Clerk session cookie on first load
  useEffect(() => {
    // Wait for Clerk to fully load before checking authentication state
    if (!isAuthLoaded) return;
    if (isCompleting || isNavigating || postAuthNavigationStartedRef.current) return;

    // If user is already authenticated, handle invitation or redirect to onboarding
    // This handles the case where:
    // 1. User has valid session but SSR route guard couldn't read the cookie
    // 2. Client-side Clerk detects the session after hydration
    // 3. We need to manually trigger navigation instead of staying on signin page
    if (auth.isSignedIn) {
      postAuthNavigationStartedRef.current = true;
      logInfo("auth.signin.already_authenticated", {
        userId: auth.userId,
        has_join_workspace: !!join_workspace,
      });

      // Handle already authenticated user with async function
      const handleAlreadyAuthenticated = async () => {
        // Ensure user exists in backend before handling invitation
        // This is crucial for new users who just completed SSO but don't have a DB record yet
        let onboardingState: string | null = null;
        try {
          const status = await getOnboardingStatusServer();
          onboardingState = status.onboarding_state ?? null;
        } catch (error) {
          logError("auth.signin.ensure_user_failed", {
            error: error instanceof Error ? error.message : "unknown",
          });
          // Even if ensure_user fails, try to continue
        }

        // Check for invitation token (prioritize URL param over localStorage)
        let invitationToken: string | null = join_workspace ?? null;
        if (!invitationToken) {
          try {
            invitationToken = localStorage.getItem(PENDING_INVITATION_KEY);
          } catch {
            // localStorage might be disabled, ignore
          }
        }

        if (invitationToken) {
          // Handle invitation acceptance for already logged-in user
          const success = await handleAcceptInvitation(invitationToken);
          if (!success) {
            // If invitation failed, redirect to onboarding
            navigate({ to: "/onboarding" });
          }
        } else {
          const targetPath =
            onboardingState === "workspace_created"
              ? (return_to ?? "/onboarding")
              : buildOnboardingReturnPath(return_to);
          window.location.replace(targetPath);
        }
      };

      void handleAlreadyAuthenticated();
    }
  }, [
    isAuthLoaded,
    isCompleting,
    isNavigating,
    auth.isSignedIn,
    auth.userId,
    join_workspace,
    return_to,
    navigate,
    handleAcceptInvitation,
  ]);

  useEffect(() => {
    const runImpersonationTicketSignIn = async () => {
      try {
        if (!isSignInLoaded || !signIn) return;
        const url = new URL(window.location.href);
        const ticket = url.searchParams.get("__clerk_ticket");
        if (!ticket) return;

        logInfo("auth.impersonation.ticket_detected", {
          has_ticket: true,
        });

        const result = await signIn.create({
          strategy: "ticket",
          ticket,
        } as unknown as { strategy: "ticket"; ticket: string });
        if (result.status === "complete" && result.createdSessionId) {
          await setActiveSignIn({
            session: result.createdSessionId,
            navigate: async () => {
              try {
                await getOnboardingStatusServer();
                logInfo("auth.impersonation.complete", {});
                navigate({ to: "/onboarding" });
              } catch (error) {
                logError("auth.ensure_user.failed", {
                  flow: "impersonation",
                  ...toErrorPayload(error, t),
                });
              }
            },
          });
        } else {
          logWarn("auth.impersonation.incomplete", {
            status: result.status,
          });
        }
      } catch (error) {
        logError("auth.impersonation.error", toErrorPayload(error, t));
      }
    };
    void runImpersonationTicketSignIn();
  }, [isSignInLoaded, signIn, setActiveSignIn, navigate, t]);

  const ensureOnboardingStatusBestEffort = useCallback(
    (flow: AuthFlow): void => {
      void getOnboardingStatusServer()
        .then(() => {
          logInfo(`auth.${flow}.ensure_user_complete`, {});
        })
        .catch((error: unknown) => {
          logError("auth.ensure_user.failed", {
            flow,
            ...toErrorPayload(error, t),
          });
        });
    },
    [t],
  );

  // Helper to handle post-authentication navigation (shared by sign-in and sign-up)
  // Must be defined before early returns to comply with React hooks rules
  const handlePostAuthNavigation = useCallback(
    async (flow: AuthFlow, setNavigationStarted: (started: boolean) => void): Promise<void> => {
      logInfo(`auth.${flow}.complete`, {});
      ensureOnboardingStatusBestEffort(flow);
      postAuthNavigationStartedRef.current = true;

      // Mark navigation as started to prevent UI state reset
      setNavigationStarted(true);
      setIsNavigating(true);

      // Check for pending invitation token
      let invitationToken: string | null = join_workspace ?? null;
      if (!invitationToken) {
        try {
          invitationToken = localStorage.getItem(PENDING_INVITATION_KEY);
        } catch {
          // localStorage might be disabled, ignore
        }
      }

      if (invitationToken) {
        // Handle invitation acceptance
        const success = await handleAcceptInvitation(invitationToken);
        if (!success) {
          // If invitation failed, fallback to onboarding with a fresh cookie read.
          setTimeout(() => window.location.replace("/onboarding"), 500);
        }
      } else {
        const targetPath =
          flow === "sign-up" ? buildOnboardingReturnPath(return_to) : (return_to ?? "/onboarding");
        // No invitation, navigate after Clerk has had a moment to persist the session cookie.
        setTimeout(() => window.location.replace(targetPath), 500);
      }
    },
    [ensureOnboardingStatusBestEffort, join_workspace, handleAcceptInvitation, return_to],
  );

  useEffect(() => {
    if (left <= 0) return;
    if (verifying && authFlow) {
      const t = setInterval(() => setLeft((n) => n - 1), 1000);
      return () => clearInterval(t);
    }
  }, [left, verifying, authFlow]);

  if (!hasMounted) {
    return null;
  }

  if (!isAuthLoaded) {
    return null;
  }

  // Show loading state while accepting invitation
  if (isAcceptingInvitation) {
    return (
      <OnboardingLayout title={t("pages:auth.slogan")}>
        <OnboardingCard className="text-center py-12">
          <Spinner className="mx-auto mb-4" />
          <p className="text-gray-500">
            {t("pages:auth.joiningWorkspace", "Joining workspace...")}
          </p>
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  const resetStateForVerification = (flow: AuthFlow) => {
    logInfo("auth.verification.transition", {
      flow,
      email,
    });
    setAuthFlow(flow);
    setCode("");
    setErrors(undefined);
    setVerifying(true);
  };

  const resetStateForSignup = () => {
    setVerifying(false);
    setAuthFlow(null);
    setEmail("");
    setErrors(undefined);
  };

  const handleResendVerificationEmail = async () => {
    if (!authFlow) {
      logWarn("auth.resend.no_flow", {});
      return;
    }

    try {
      if (!isSignInLoaded || !signIn) {
        logWarn("auth.resend.signin_not_loaded", {});
        return;
      }

      const { supportedFirstFactors } = signIn;
      const emailCodeFactor = supportedFirstFactors?.find(isEmailCodeFactor);

      if (!emailCodeFactor) {
        throw new Error(t("pages:auth.emailCodeUnavailable"));
      }

      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailCodeFactor.emailAddressId,
      });
      logInfo("auth.resend.signin_success", { email });

      setLeft(seconds);
      setResendStatus(true);
      setErrors(undefined);

      // Reset resend status after 2 seconds to show countdown
      setTimeout(() => {
        setResendStatus(false);
      }, 2000);
    } catch (error) {
      logError("auth.resend.failed", {
        error: toErrorPayload(error, t),
        authFlow,
        email,
      });

      if (isClerkAPIResponseError(error)) {
        setErrors(error.errors);
      } else {
        setErrors([{ message: t("pages:auth.resendFailed") } as ClerkAPIError]);
      }
      setResendStatus(false);
    }
  };

  const handleSignIn = async (identifier: string) => {
    if (!isSignInLoaded || !signIn) {
      logWarn("auth.signin.resource_not_loaded", {});
      return;
    }

    logInfo("auth.signin.initiated", { identifier });

    const { supportedFirstFactors } = await signIn.create({ identifier });
    const emailCodeFactor = supportedFirstFactors?.find(isEmailCodeFactor);

    if (!emailCodeFactor) {
      throw new Error(t("pages:auth.emailCodeUnavailable"));
    }

    await signIn.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: emailCodeFactor.emailAddressId,
    });

    logInfo("auth.signin.factor_prepared", {
      emailAddressId: emailCodeFactor.emailAddressId,
    });
  };

  const handleSignUp = async (emailAddress: string) => {
    if (!isSignUpLoaded || !signUp) {
      logWarn("auth.signup.resource_not_loaded", {});
      return;
    }

    logInfo("auth.signup.initiated", { emailAddress });

    await signUp.create({ emailAddress });
    await signUp.prepareEmailAddressVerification({
      strategy: "email_code",
    });

    logInfo("auth.signup.verification_prepared", { emailAddress });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors(undefined);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    logInfo("auth.email_submitted", { email: trimmedEmail });
    setIsProcessing(true);

    try {
      await handleSignIn(trimmedEmail);
      resetStateForVerification("sign-in");
    } catch (err) {
      if (isIdentifierNotFoundError(err)) {
        // User not found - automatically switch to sign-up flow
        // Note: We don't log "identifier_not_found" to avoid exposing user enumeration
        logInfo("auth.email.verification_sent", {
          email: trimmedEmail,
        });

        try {
          await handleSignUp(trimmedEmail);
          resetStateForVerification("sign-up");
        } catch (signUpErr) {
          if (isClerkAPIResponseError(signUpErr)) setErrors(signUpErr.errors);
          logError("auth.signup.error", toErrorPayload(signUpErr, t));
        }
        return;
      }

      if (isClerkAPIResponseError(err)) setErrors(err.errors);
      logError("auth.signin.error", toErrorPayload(err, t));
    } finally {
      setIsProcessing(false);
    }
  };

  const submitVerificationCode = async (verificationCode: string): Promise<void> => {
    if (!authFlow) return;
    if (verificationCode.length !== 6) return;
    if (isCompleting || isNavigating || verificationSubmittingRef.current) return;

    verificationSubmittingRef.current = true;
    setErrors(undefined);
    setIsCompleting(true);

    logInfo("auth.verification.submitted", {
      flow: authFlow,
      codeLength: verificationCode.length,
    });

    let navigationStarted = false;
    const setNavigationStarted = (started: boolean) => {
      navigationStarted = started;
    };

    try {
      if (authFlow === "sign-in") {
        if (!isSignInLoaded || !signIn) return;

        const signInAttempt = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code: verificationCode,
        });

        if (signInAttempt.status === "complete" && signInAttempt.createdSessionId) {
          let pendingTaskDetected = false;
          await setActiveSignIn({
            session: signInAttempt.createdSessionId,
            navigate: async ({ session }) => {
              if (session?.currentTask) {
                pendingTaskDetected = true;
                logInfo("auth.session.pending_task", {
                  task: session.currentTask,
                });
                return;
              }
              await handlePostAuthNavigation("sign-in", setNavigationStarted);
            },
          });

          if (!navigationStarted && !pendingTaskDetected) {
            logWarn("auth.signin.navigate_callback_missing", {
              reason: "setActiveSignIn completed without starting navigation",
            });
            await handlePostAuthNavigation("sign-in", setNavigationStarted);
          }
        } else {
          logWarn("auth.signin.attempt_incomplete", {
            status: signInAttempt.status,
          });
        }
      } else if (authFlow === "sign-up") {
        if (!isSignUpLoaded || !signUp) return;

        let signUpAttempt = await signUp.attemptEmailAddressVerification({
          code: verificationCode,
        });

        // Handle missing requirements (likely First/Last Name required by Clerk)
        if (signUpAttempt.status === "missing_requirements") {
          const missing = signUpAttempt.missingFields || [];
          logWarn("auth.signup.missing_requirements", { missing });

          // If only name is missing, auto-fill it to unblock the user
          // This happens when Clerk requires name but our UI only collects email
          const isNameMissing = missing.some((f) => ["first_name", "last_name"].includes(f));
          const isPasswordMissing = missing.includes("password");

          // Filter out fields we are about to handle
          const unhandledMissing = missing.filter(
            (f) => !["first_name", "last_name", "password"].includes(f),
          );

          if ((isNameMissing || isPasswordMissing) && unhandledMissing.length === 0) {
            const namePart = email.split("@")[0] || "User";
            logInfo("auth.signup.autofilling", { isNameMissing, isPasswordMissing });

            try {
              const updateParams: { firstName?: string; lastName?: string; password?: string } = {};

              if (isNameMissing) {
                updateParams.firstName = namePart;
                updateParams.lastName = "User";
              }

              if (isPasswordMissing) {
                // Generate a random secure password for the user since they are using email code auth
                // They can reset it later or just keep using email codes
                const randomPassword =
                  Math.random().toString(36).slice(-8) +
                  Math.random().toString(36).slice(-8) +
                  "Aa1!";
                updateParams.password = randomPassword;
              }

              signUpAttempt = await signUp.update(updateParams);
            } catch (updateErr) {
              logError("auth.signup.autofill_failed", toErrorPayload(updateErr, t));
            }
          }
        }

        if (signUpAttempt.status === "complete" && signUpAttempt.createdSessionId) {
          await setActiveSignUp({
            session: signUpAttempt.createdSessionId,
            navigate: () => Promise.resolve(),
          });
          await handlePostAuthNavigation("sign-up", setNavigationStarted);
        } else {
          logWarn("auth.signup.attempt_incomplete", {
            status: signUpAttempt.status,
            missingFields: signUpAttempt.missingFields,
          });
          setErrors([
            {
              code: "signup_incomplete",
              message:
                "Sign up incomplete. Missing: " +
                (signUpAttempt.missingFields?.join(", ") || "unknown requirements"),
            },
          ]);
        }
      }
    } catch (err) {
      if (isSessionAlreadyExistsError(err)) {
        logWarn("auth.verification.session_already_exists", {
          flow: authFlow,
          ...toErrorPayload(err, t),
        });
        await handlePostAuthNavigation(authFlow, setNavigationStarted);
        return;
      }

      if (isClerkAPIResponseError(err)) {
        // Check if the error is related to incorrect verification code
        const hasIncorrectCodeError = err.errors.some(
          (error) =>
            error.code === "form_code_incorrect" ||
            error.message?.toLowerCase().includes("incorrect") ||
            error.message?.toLowerCase().includes("invalid"),
        );

        if (hasIncorrectCodeError) {
          // Replace with user-friendly message
          setErrors([
            {
              code: "verification_code_incorrect",
              message: t("pages:auth.verificationCodeIncorrect"),
            } as ClerkAPIError,
          ]);
        } else {
          setErrors(err.errors);
        }
      } else {
        // Fallback for non-Clerk errors
        setErrors([
          {
            code: "verification_failed",
            message: t("pages:auth.verificationFailed"),
          } as ClerkAPIError,
        ]);
      }
      logError("auth.verification.error", toErrorPayload(err, t));
    } finally {
      // Only reset isCompleting if navigation hasn't started
      // This prevents the verification form from flashing before navigation completes
      if (!navigationStarted) {
        setIsCompleting(false);
        verificationSubmittingRef.current = false;
      }
    }
  };

  const handleVerification = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await submitVerificationCode(code);
  };

  const handleGoogleSignIn = async () => {
    if (!isSignInLoaded || !signIn) {
      logWarn("auth.google.resource_not_loaded", {});
      return;
    }

    setIsGoogleLoading(true);
    setErrors(undefined);

    try {
      const redirectUrl = `${window.location.origin}/signin/sso-callback`;
      const redirectUrlComplete = `${window.location.origin}${buildOnboardingReturnPath(return_to)}`;

      logInfo("auth.google.initiated", {
        redirectUrl,
        redirectUrlComplete,
        origin: window.location.origin,
      });

      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (err) {
      if (isClerkAPIResponseError(err)) setErrors(err.errors);
      logError("auth.google.error", toErrorPayload(err, t));
      setIsGoogleLoading(false);
    }
  };

  if (verifying && authFlow) {
    // Use neutral labels to avoid exposing whether user exists or not
    const submitLabel = t("pages:auth.continue");
    const submitLoadingLabel = t("pages:auth.verifying");

    return (
      <OnboardingLayout title={t("pages:auth.slogan")}>
        <OnboardingDescription>
          <p className="text-[20px] text-[#666666]/80 whitespace-nowrap">
            {t("pages:auth.codeSent")}
          </p>
        </OnboardingDescription>
        <OnboardingCard>
          <div className="text-center text-gray-500 mb-10">"{email}"</div>
          <form className="space-y-6" onSubmit={handleVerification}>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value: string) => {
                  // only numbers, 6 length limit
                  if (/^\d{0,6}$/.test(value)) {
                    setCode(value);
                  }
                }}
                onComplete={(value: string) => {
                  if (/^\d{6}$/.test(value)) {
                    void submitVerificationCode(value);
                  }
                }}
                pattern="[0-9]*"
                inputMode="numeric"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {!!errors?.length && (
              <div className="text-sm text-red-500 text-center">
                {errors.map((error) => (
                  <p key={error.code}>{error.message}</p>
                ))}
              </div>
            )}

            <p className="flex justify-center text-gray-400 mt-10 mb-2">
              {t("pages:auth.didYouReceive")}
              {resendStatus ? (
                <span className="pl-1.5 text-green-500">{t("pages:auth.resendSuccess")}</span>
              ) : left > 0 ? (
                <span className="pl-1.5">{t("pages:auth.resendCount", { seconds: left })}</span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendVerificationEmail}
                  style={{
                    color: "#2563eb",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    paddingLeft: "6px",
                  }}
                >
                  {t("pages:auth.resend")}
                </button>
              )}
            </p>
            <p className="flex justify-center">
              <button
                type="button"
                onClick={() => resetStateForSignup()}
                className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
              >
                {t("pages:auth.useAnotherEmail")}
              </button>
            </p>
            {code.length === 6 && (
              <OnboardingButton
                type="submit"
                variant="default"
                disabled={isCompleting || isNavigating}
              >
                {isCompleting || isNavigating ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner />
                    {submitLoadingLabel}
                  </span>
                ) : (
                  <span>{submitLabel}</span>
                )}
              </OnboardingButton>
            )}
          </form>
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout title={t("pages:auth.slogan")} description={t("pages:auth.subSlogan")}>
      <OnboardingCard>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <OnboardingLabel htmlFor="email">{t("pages:auth.emailLabel")}</OnboardingLabel>
            <OnboardingInput
              value={email}
              id="email"
              name="email"
              type="email"
              placeholder={t("pages:auth.emailPlaceholder")}
              onChange={(e) => setEmail(e.target.value)}
              icon={<AtSign className="w-5 h-5" />}
              autoComplete="email"
            />
          </div>

          {!!errors?.length && (
            <div className="text-sm text-red-500 text-center">
              {errors.map((error) => (
                <p key={error.code}>{error.message}</p>
              ))}
            </div>
          )}

          <OnboardingButton
            className="mb-0"
            type="submit"
            variant="default"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="w-5 h-5" />
                {t("pages:auth.preparingWorkspace")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {t("pages:auth.continueEmail")}
              </span>
            )}
          </OnboardingButton>

          <OnboardingDivider />

          <OnboardingButton
            variant="secondary"
            className="mb-2"
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading || isProcessing}
          >
            {isGoogleLoading ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="w-5 h-5" />
                {t("pages:auth.connectingGoogle")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <GoogleIcon className="w-5 h-5" />
                {t("pages:auth.continueGoogle")}
              </span>
            )}
          </OnboardingButton>

          <OnboardingTerms />
        </form>
      </OnboardingCard>
    </OnboardingLayout>
  );
}
